<template>
  <div>
    <v-btn color="primary" class="mb-4 mr-5" @click="exportProfilngAnswers">
      Export Individual Responses
    </v-btn>
    <v-btn color="primary" class="mb-4" @click="exportTotalResponses">
      Export Total Responses
    </v-btn>
    <!-- Filters Expansion Panel -->
    <v-expansion-panels multiple>
      <v-expansion-panel>
        <v-expansion-panel-header> Filter Responses </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <!-- Gender Filter -->
            <v-col cols="12" md="4">
              <v-select
                v-model="filters.gender"
                :items="['Male', 'Female']"
                label="Gender"
                outlined
                dense
                clearable
              />
            </v-col>

            <!-- Age Range Min -->
            <v-col cols="6" md="4">
              <v-text-field
                v-model.number="filters.ageRange.min"
                type="number"
                label="Min Age"
                outlined
                dense
              />
            </v-col>

            <!-- Age Range Max -->
            <v-col cols="6" md="4">
              <v-text-field
                v-model.number="filters.ageRange.max"
                type="number"
                label="Max Age"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" md="4">
              <v-select
                v-model="filters.country"
                :items="['Malaysia', 'Indonesia', 'Singapore']"
                label="Country"
                outlined
                dense
                clearable
              />
            </v-col>

            <v-col cols="6" md="4" v-if="filters.country==='Malaysia'">
              <v-select
                v-model="filters.race"
                :items="['Malay', 'Chinese', 'Indian']"
                label="Race"
                outlined
                dense
                clearable
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-2">
            <v-btn class="mr-5" color="primary" @click="applyFilters">
              Apply Filters
            </v-btn>
            <v-btn color="primary" @click="resetFilter"> Reset </v-btn>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Main Survey Responses -->
    <SurveyChart
      v-for="(item, index) in responses"
      :key="index"
      :question="item.question"
      :answers="item.answers"
      :totalResponses="item.totalResponses"
    />

    <!-- Compare Button -->
    <v-btn color="primary" class="mt-4" @click="openCompareModal">
      Compare
    </v-btn>

    <!-- Compare Modal -->
    <v-dialog v-model="showCompareModal" max-width="700px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          Compare Surveys
          <v-btn icon @click="showCompareModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-select
            v-model="selectedSurveyId"
            :items="surveyList"
            item-text="label"
            item-value="_id"
            label="Select a survey to compare"
            outlined
            dense
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCompareModal = false">Cancel</v-btn>
          <v-btn color="primary" @click="fetchComparison">Compare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Comparison + Individual Charts -->
    <div v-if="comparisonReady" class="mt-8">
      <!-- Comparison Chart -->
      <v-card class="mb-8">
        <v-card-title class="text-h6 font-weight-medium">
          Comparison Chart
        </v-card-title>
        <v-card-text>
          <Chart :options="comparisonChartOptions" />
        </v-card-text>
      </v-card>

      <!-- Individual Charts Side by Side -->
      <v-row>
        <!-- Base Survey -->
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title class="text-h6 font-weight-medium">
              {{ baseSurveyTitle }}
            </v-card-title>
            <v-card-text>
              <Chart :options="baseSurveyChartOptions" />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Compared Survey -->
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title class="text-h6 font-weight-medium">
              {{ comparedSurveyTitle }}
            </v-card-title>
            <v-card-text>
              <Chart :options="comparedSurveyChartOptions" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2";
import SurveyChart from "./components/SurveyChart.vue";
import { Chart } from "highcharts-vue";
import * as XLSX from "xlsx";

const service = new RestResource();

export default {
  components: {
    SurveyChart,
    Chart,
  },
  data() {
    return {
      responses: [],
      showCompareModal: false,
      surveyList: [],
      selectedSurveyId: null,
      comparisonReady: false,
      comparisonChartOptions: null,
      baseSurveyChartOptions: null,
      comparedSurveyChartOptions: null,
      baseSurveyTitle: "",
      comparedSurveyTitle: "",
      filters: {
        gender: null,
        ageRange: { min: null, max: null },
        country: null,
      },
      defaultFilters: {
        gender: null,
        ageRange: { min: null, max: null },
        country: null,
      },
    };
  },

  mounted() {
    const surveyId = this.$route.params.surveyId;
    if (surveyId) {
      this.getResponse(surveyId);
    }
  },

  methods: {
    getResponse(surveyId) {
      this.$setLoader();
      service
        .getProfilingQuestionResponses({
          surveyId,
          gender: this.filters.gender,
          ageRange: this.filters.ageRange,
          country: this.filters.country,
          race: this.filters.race
        })
        .then((r) => {
          this.responses = r.data.data;
          this.$disableLoader();
        });
    },

    applyFilters() {
      const surveyId = this.$route.params.surveyId;
      if (surveyId) {
        this.getResponse(surveyId);
      }
    },

    resetFilter() {
      this.filters = JSON.parse(JSON.stringify(this.defaultFilters));
      this.getResponse(this.$route.params.surveyId);
    },

    openCompareModal() {
      this.showCompareModal = true;
      service
        .getSurveysForComparision({ surveyId: this.$route.params.surveyId })
        .then((r) => {
          this.surveyList = r.data.data.map((s) => ({
            _id: s._id,
            label: `${s.name}: ${s.questions[0] || "No question"}`,
          }));
        });
    },

    fetchComparison() {
      if (!this.selectedSurveyId) return;

      this.$setLoader();
      service
        .getComparisionData({
          baseSurveyId: this.$route.params.surveyId,
          compareSurveyId: this.selectedSurveyId,
        })
        .then((r) => {
          const data = r.data.data;
          // assume backend gives { categories, surveyA, surveyB }

          // Titles (fallback if backend doesn’t send them)
          this.baseSurveyTitle = data.surveyA?.title || "Base Survey";
          this.comparedSurveyTitle = data.surveyB?.title || "Compared Survey";

          // Comparison Chart
          this.comparisonChartOptions = {
            chart: { type: "column", backgroundColor: "transparent" },
            title: { text: "Survey Comparison" },
            xAxis: { categories: data.categories },
            yAxis: { min: 0, title: { text: "Responses" } },
            legend: { align: "center", verticalAlign: "bottom" },
            tooltip: { shared: true },
            plotOptions: {
              column: {
                grouping: true,
                borderWidth: 0,
                dataLabels: { enabled: true },
              },
            },
            series: [
              {
                name: this.baseSurveyTitle,
                data: data.surveyA.data,
                color: "#1976d2",
              },
              {
                name: this.comparedSurveyTitle,
                data: data.surveyB.data,
                color: "#ff9800",
              },
            ],
          };

          // Base Survey Standalone
          this.baseSurveyChartOptions = {
            chart: { type: "pie", backgroundColor: "transparent" },
            title: { text: this.baseSurveyTitle },
            series: [
              {
                name: "Responses",
                colorByPoint: true,
                data: data.surveyA.data.map((v, i) => ({
                  name: data.categories[i],
                  y: v,
                })),
              },
            ],
          };

          // Compared Survey Standalone
          this.comparedSurveyChartOptions = {
            chart: { type: "pie", backgroundColor: "transparent" },
            title: { text: this.comparedSurveyTitle },
            series: [
              {
                name: "Responses",
                colorByPoint: true,
                data: data.surveyB.data.map((v, i) => ({
                  name: data.categories[i],
                  y: v,
                })),
              },
            ],
          };

          this.showCompareModal = false;
          this.comparisonReady = true;
          this.$disableLoader();
        });
    },

    async exportProfilngAnswers() {
      this.$setLoader();
      try {
        const response = await service.exportProfilngAnswers({
          surveyId: this.$route.params.surveyId,
          gender: this.filters.gender,
          ageRange: this.filters.ageRange,
          country: this.filters.country,
          race: this.filters.race
        });
        const data = response.data.data;

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Responses");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "profiling-answers.xlsx");
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        this.$disableLoader();
      }
    },

    async exportTotalResponses() {
      this.$setLoader();
      try {
        const response = await service.exportTotalResponses({
          surveyId: this.$route.params.surveyId,
        });

        const data = response.data.data;

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Responses");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "total-responses.xlsx");
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log(response.data.data);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        this.$disableLoader();
      }
    },
  },
};
</script>
